import React, { useEffect, useMemo, useState } from 'react';
import DataGrid from '../DataGrid/DataGrid.jsx';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import api from '../../../utils/helpers/bootstrapApi.jsx';
import {
  DATETIME_FORMAT_DATE,
  friEduGetEnrollDisabledReason,
  friEduGetUserParticipantId,
  friEduIsUserEnrolled,
  resolveFriEduDateStatus,
} from '../../../utils/helpers/CommonHelper.jsx';
import { FRI_EDU_DATE_STATUS } from '../../../utils/constants/enums.js';
import { IconButton } from '../controls/Controls.jsx';
import { QuestionModal } from '../index.jsx';
import {
  EditOutlined,
  DeleteFilled,
  StopOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { routes } from '../../../utils/helpers/ApiClient.jsx';
import { useToastMessages } from '../../../hooks/useToastMessages.jsx';
import {
  dataGridDateSorter,
  dataGridNumberSorter,
  dataGridTextSorter,
} from '../DataGrid/sorters/DataGridSorters.jsx';
import { useDataGridTextFilter } from '../DataGrid/filters/useDataGridTextFilter.jsx';
import { useDataGridDateRangeFilter } from '../DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { useOptionsDataGridFilter } from '../DataGrid/filters/DataGridFilters.jsx';
import {
  useDeleteEduCourseDateMutation,
  useCancelEduCourseDateMutation,
} from '../../../redux/services/eduCourseDates.jsx';
import useUserHasRole from '../../../hooks/useUserHasRoles.jsx';
import { useSelector } from 'react-redux';
import { ROLE_FRI_EDU_PATRICIPANT } from '../../../utils/constants/roles.js';
import { useCourseDateEnrollMutations } from '../../../hooks/useCourseDateEnrollMutations.jsx';
import {
  StyledModal,
  StyledModalHeader,
  ButtonContainer,
  MainButton,
} from '../form/FormElements.jsx';
import { SeriousDeleteQuestion } from '../DeleteQuestion/SeriousDeleteQuestion.jsx';

const resolveDateStatus = (state) => resolveFriEduDateStatus(state);

// Helpers to determine enroll availability

const getEnrollDisabledReason = (row, t) =>
  friEduGetEnrollDisabledReason(row, t);

// Helpers for current user's enrollment on the date
const getUserParticipantId = (row) => friEduGetUserParticipantId(row);
const isUserEnrolledToDate = (row) => friEduIsUserEnrolled(row);

const EduCourseDatesNestedTable = ({
  courseDetail,
  dates,
  isAdmin,
  isAuthor,
  canUpdate,
  onRefetchCourses,
  isPublic = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { successMessage, errorMessage } = useToastMessages();

  const isLoggedIn = useSelector((s) => s.auth.isLoggedIn);
  const hasParticipantRole = useUserHasRole(ROLE_FRI_EDU_PATRICIPANT);

  const courseId = courseDetail?.id;

  const [rows, setRows] = useState(Array.isArray(dates) ? dates : []);
  const [deleteId, setDeleteId] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  const [authModalVisible, setAuthModalVisible] = useState(false);
  const [pendingDateId, setPendingDateId] = useState(null);
  const [rowToUnenroll, setRowToUnenroll] = useState(null);

  useEffect(() => {
    setRows(Array.isArray(dates) ? dates : []);
  }, [dates]);

  const [deleteCourseDate] = useDeleteEduCourseDateMutation();
  const [cancelCourseDate] = useCancelEduCourseDateMutation();

  const { enrollToCourseDate, unenrollFromCourseDate } =
    useCourseDateEnrollMutations({
      onEnrollmentChange: async () => {
        if (typeof onRefetchCourses === 'function') await onRefetchCourses();
      },
    });

  const canAct = isAdmin || (isAuthor && canUpdate);

  // PDF download handler
  const handleDownloadPDF = async (row) => {
    try {
      const url = `/api/fri-edu/course-dates/${row.id}/description-pdf`;

      // Use axios to get JSON response with Base64 PDF
      const response = await api({
        url,
        method: 'GET',
      });

      // Decode Base64 string to binary
      const byteCharacters = atob(response.data.pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });

      // Create download link
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download =
        response.data.filename ||
        `Popis_kurzu_${courseDetail?.name || 'kurz'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      errorMessage({
        message: t('Chyba pri sťahovaní PDF'),
        description: t(
          'Nepodarilo sa stiahnuť popis kurzu. Skúste to prosím znova.',
        ),
      });
    }
  };

  const placeFilterProps = useDataGridTextFilter('place');
  const timeFilterProps = useDataGridTextFilter('time');
  const dateFromFilterProps = useDataGridDateRangeFilter('date_from');
  const dateToFilterProps = useDataGridDateRangeFilter('date_to');
  const occupancyFilterProps = useDataGridTextFilter('occupancy');

  const statusOptions = useMemo(() => {
    const map = {};
    Object.values(FRI_EDU_DATE_STATUS).forEach((v) => {
      if (v && typeof v === 'object' && 'STATE' in v) {
        map[v.STATE] = t(v.LABEL);
      }
    });
    return map;
  }, [t]);
  const statusFilterProps = useOptionsDataGridFilter('status', statusOptions);

  const onConfirmDelete = async () => {
    try {
      await deleteCourseDate(deleteId).unwrap();
      setRows((prev) => prev.filter((r) => r.id !== deleteId));
      // Refetch parent courses & dates after deletion
      if (typeof onRefetchCourses === 'function') {
        await onRefetchCourses();
      }
      successMessage({ message: t('Termín bol úspešne vymazaný') });
    } catch (e) {
      errorMessage({ message: t('Vymazanie termínu zlyhalo') });
    } finally {
      setDeleteId(null);
    }
  };

  const onConfirmCancel = async () => {
    try {
      await cancelCourseDate(cancelId).unwrap();
      setRows((prev) =>
        prev.map((r) =>
          r.id === cancelId ? { ...r, status: 'cancelled' } : r,
        ),
      );
      successMessage({ message: t('Termín bol úspešne zrušený') });
      if (typeof onRefetchCourses === 'function') {
        await onRefetchCourses();
      }
    } catch (e) {
      errorMessage({ message: t('Zrušenie termínu zlyhalo') });
    } finally {
      setCancelId(null);
    }
  };

  // New helper to cancel immediately (used when occupancy is 0)
  const performImmediateCancel = async (id) => {
    try {
      await cancelCourseDate(id).unwrap();
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: 'cancelled' } : r)),
      );
      successMessage({ message: t('Termín bol úspešne zrušený') });
      if (typeof onRefetchCourses === 'function') {
        await onRefetchCourses();
      }
    } catch (e) {
      errorMessage({ message: t('Zrušenie termínu zlyhalo') });
    }
  };

  const columns = useMemo(
    () => [
      {
        title: t('Od'),
        dataIndex: 'date_from',
        key: 'date_from',
        awidth: 140,
        render: (v) => (v ? dayjs(v).format(DATETIME_FORMAT_DATE) : ''),
        sorter: dataGridDateSorter('date_from'),
        defaultSortOrder: 'ascend',
        ...dateFromFilterProps,
      },
      {
        title: t('Do'),
        dataIndex: 'date_to',
        key: 'date_to',
        awidth: 140,
        render: (v) => (v ? dayjs(v).format(DATETIME_FORMAT_DATE) : ''),
        sorter: dataGridDateSorter('date_to'),
        ...dateToFilterProps,
      },
      {
        title: t('Čas'),
        dataIndex: 'time',
        key: 'time',
        width: 120,
        sorter: dataGridTextSorter('time'),
        ...timeFilterProps,
      },
      {
        title: t('Obsadenosť'),
        key: 'occupancy_col',
        width: 160,
        align: 'right',
        render: (_, row) => `${row?.occupancy ?? 0}/${row?.capacity ?? '-'}`,
        sorter: dataGridNumberSorter('occupancy', (v) => parseInt(v ?? 0, 10)),
        ...occupancyFilterProps,
      },
      {
        title: t('Miesto'),
        dataIndex: 'place',
        key: 'place',
        ellipsis: true,
        sorter: dataGridTextSorter('place'),
        ...placeFilterProps,
      },
      {
        title: t('Stav'),
        dataIndex: 'status',
        key: 'status',
        width: 160,
        render: (status) => {
          const cfg = resolveDateStatus(status);
          return <Tag color={cfg.COLOR}>{t(cfg.LABEL)}</Tag>;
        },
        sorter: dataGridTextSorter(
          'status',
          (s) => resolveDateStatus(s)?.LABEL || '',
        ),
        ...statusFilterProps,
      },
    ],
    [
      t,
      dateFromFilterProps,
      dateToFilterProps,
      timeFilterProps,
      occupancyFilterProps,
      placeFilterProps,
      statusFilterProps,
    ],
  );

  const canShowEnrollAction = (/*row*/) => {
    // visible when user not logged in OR has participant role
    return !isLoggedIn || hasParticipantRole;
  };

  const handleEnrollClick = async (row) => {
    if (!isLoggedIn) {
      setPendingDateId(row?.id ?? null);
      setAuthModalVisible(true);
      return;
    }
    if (hasParticipantRole) {
      try {
        await enrollToCourseDate(row.id);
      } catch (e) {
        // error toast handled in hook
      }
    }
  };

  const handleUnenrollClick = (row) => {
    const participantId = getUserParticipantId(row);
    if (!participantId) {
      errorMessage({
        message: t(
          'Nepodarilo sa odhlásiť z termínu: chýba identifikátor prihlásenia',
        ),
      });
      return;
    }
    setRowToUnenroll({ row, participantId });
  };

  // Enrollment action-only settings (public view)
  const EnrollOnlySettings = ({ row }) => {
    const enrolled = isLoggedIn && isUserEnrolledToDate(row);
    if (enrolled) {
      return (
        <div className="d-flex">
          <IconButton
            icon={<UserDeleteOutlined />}
            tooltip={t('Odhlásiť sa z termínu')}
            onClick={(e) => {
              e.stopPropagation();
              handleUnenrollClick(row);
            }}
          />
        </div>
      );
    }

    // Disable enroll when author of the course
    const authorEnrollBlockReason = isAuthor
      ? t('Hlavný lektor kurzu sa nemôže prihlásiť')
      : null;
    const computedReason =
      authorEnrollBlockReason ?? getEnrollDisabledReason(row, t);
    const disabled = !!computedReason;

    return (
      <div className="d-flex">
        {canShowEnrollAction(row) && (
          <IconButton
            icon={<UserAddOutlined />}
            tooltip={t('Prihlásiť sa na termín')}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) handleEnrollClick(row);
            }}
            disabled={disabled}
            disabledTooltip={computedReason || undefined}
          />
        )}
      </div>
    );
  };

  // Combined settings (CMS view): edit first, then enroll/unenroll, then other admin/author actions
  const CombinedSettings = ({ row }) => {
    const enrolled = isLoggedIn && isUserEnrolledToDate(row);

    return (
      <div className="d-flex">
        {/* Edit icon always first when user can act */}
        {canAct && (
          <IconButton
            icon={<EditOutlined />}
            tooltip={t('Upraviť')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${routes.cmsEduDates}/${courseId}/edit/${row.id}`);
            }}
          />
        )}

        {/* PDF download for course lecturer or admin only */}
        {(isAuthor || isAdmin) && (
          <IconButton
            icon={<FilePdfOutlined />}
            tooltip={t('Stiahnuť popis kurzu v PDF')}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadPDF(row);
            }}
          />
        )}

        {/* Enroll / Unenroll icon next */}
        {enrolled ? (
          <IconButton
            icon={<UserDeleteOutlined />}
            tooltip={t('Odhlásiť sa z termínu')}
            onClick={(e) => {
              e.stopPropagation();
              handleUnenrollClick(row);
            }}
          />
        ) : (
          (() => {
            const authorEnrollBlockReason = isAuthor
              ? t('Hlavný sa lektor kurzu nemôže prihlásiť na vlastný kurz')
              : null;
            const reason =
              authorEnrollBlockReason ?? getEnrollDisabledReason(row, t);
            const disabled = !!reason;
            return (
              <IconButton
                icon={<UserAddOutlined />}
                tooltip={t('Prihlásiť sa na termín')}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled) handleEnrollClick(row);
                }}
                disabled={disabled}
                disabledTooltip={reason || undefined}
              />
            );
          })()
        )}

        {/* Remaining admin/author actions (if canAct) */}
        {canAct && (
          <>
            <IconButton
              icon={<TeamOutlined />}
              tooltip={t('Účastníci')}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${routes.cmsEduDateParticipants}/${row.id}`);
              }}
              disabled={(row.occupancy ?? 0) === 0}
              disabledTooltip={t('Termín nemá prihlásených účastníkov')}
            />
            <IconButton
              icon={<StopOutlined />}
              tooltip={t('Zrušiť termín')}
              onClick={(e) => {
                e.stopPropagation();
                if ((row.occupancy ?? 0) === 0) {
                  performImmediateCancel(row.id);
                } else {
                  setCancelId(row.id);
                }
              }}
              disabled={row.status === FRI_EDU_DATE_STATUS.CANCELLED.STATE}
              disabledTooltip={t('Termín je už zrušený')}
            />
            <IconButton
              icon={<DeleteFilled />}
              tooltip={t('Vymazať')}
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(row.id);
              }}
              disabled={(row.occupancy ?? 0) > 0}
              disabledTooltip={t('Termín už má prihlásených účastníkov')}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '8px 16px' }}>
      <DataGrid
        rowKey={(row) => row.id ?? `${row.date_from}-${row.date_to}`}
        columns={columns}
        dataSource={rows}
        permissionsBasic={'fri-edu-course-dates'}
        uniqueTableId={`fri-edu-course-terms-${courseId}`}
        disableExpandRow={() => true}
        allowClick={true}
        disableSettings={false}
        customSettings={
          isPublic
            ? (row) => <EnrollOnlySettings row={row} />
            : (row) => <CombinedSettings row={row} />
        }
        onRowClick={(row) => {
          if (canAct) {
            navigate(`${routes.cmsEduDates}/${courseId}/edit/${row.id}`);
          }
        }}
        pagination={{ pageSize: 5, placement: ['bottomRight'] }}
      />

      <QuestionModal
        visible={deleteId !== null}
        toggle={() => setDeleteId(null)}
        chosenPositive={onConfirmDelete}
        headerTitle={t('Vymazať termín')}
        question={t('Naozaj chcete vymazať tento termín?')}
        yes={t('Áno')}
      />

      <QuestionModal
        visible={cancelId !== null}
        toggle={() => setCancelId(null)}
        chosenPositive={onConfirmCancel}
        headerTitle={t('Zrušiť termín')}
        question={
          <SeriousDeleteQuestion
            firstText={t('Naozaj chcete zrušiť tento termín?')}
            mainNotice={t(
              'S termínom sa vymažú všetky prihlášky účastníkov a všetkým účastníkom sa rozpošle mail o zrušení termínu!',
            )}
            lastText={t(
              'Zoznam účastníkov bude nenávratne zmazaný. Operácia sa nedá vrátiť späť!',
            )}
          />
        }
      />
      <QuestionModal
        visible={rowToUnenroll !== null}
        toggle={() => setRowToUnenroll(null)}
        chosenPositive={async () => {
          try {
            const { row, participantId } = rowToUnenroll;
            await unenrollFromCourseDate({ dateId: row.id, participantId });
          } finally {
            setRowToUnenroll(null);
          }
        }}
        headerTitle={t('Odhlásiť sa z termínu')}
        question={t('Naozaj chcete odhlásiť sa z tohto termínu?')}
        yes={t('Áno')}
      />

      <StyledModal
        open={authModalVisible}
        size="xs"
        onCancel={() => setAuthModalVisible(false)}
        footer={null}
      >
        <StyledModalHeader>{t('Prihlásiť sa na termín')}</StyledModalHeader>
        <article style={{ padding: '0 0 20px 0' }}>
          {t(
            'Pre prihlásenie na termín sa prosím najskôr prihláste alebo si vytvorte konto.',
          )}
        </article>
        <ButtonContainer>
          <MainButton onClick={() => setAuthModalVisible(false)}>
            {t('Zrušiť')}
          </MainButton>
          <MainButton
            onClick={() => {
              // persist the date to open after redirect/login
              if (pendingDateId) {
                try {
                  localStorage.setItem(
                    'edu-open-date-id',
                    String(pendingDateId),
                  );
                } catch {}
              }
              setAuthModalVisible(false);
              // redirect to CMS My Dates page as requested
              window.location.assign(routes.cmsEduMyDates);
            }}
          >
            {t('Prihlásiť sa')}
          </MainButton>
          <MainButton
            onClick={() => {
              setAuthModalVisible(false);
              navigate('/registracia-externeho-pouzivatela');
            }}
          >
            {t('Registrovať sa')}
          </MainButton>
        </ButtonContainer>
      </StyledModal>
    </div>
  );
};

export default EduCourseDatesNestedTable;
